<template>
  <div class="buy-sell-control">
    <trade-buy-sell-header
      :trade-type="tradeType"
      :title="formattedTradeType"
      :main-ticker="firstTicker"
      :balance="formattedBalance"
      :theme="submitTheme"
      class="buy-sell-control__head"
    />

    <form
      autocomplete="off"
      class="pb-20 pt-16 pl-10 pr-10"
      @submit.prevent="handleSubmit"
      @keydown.enter.prevent
    >
      <div class="buy-sell-control__flex">
        <trade-buy-sell-input
          v-model="price"
          :field="$v.price"
          :disabled="priceInputDisabled"
          :placeholder="$t('trade.buySell.price')"
          :currency="secondTicker"
          :optional-errors="{
            ...mix_getServerError('price'),
            ...commonValidationTranslations,
          }"
          :size="size"
          autocomplete="off"
          class="buy-sell-control__price mb-16"
          type="number"
          step="0.00000001"
          @input="onChangeInputPrice"
          @blur="$v.price.$touch()"
        />
        <trade-buy-sell-input
          v-model="amount"
          :field="$v.amount"
          :placeholder="$t('trade.buySell.amount')"
          :currency="firstTicker"
          :optional-errors="{
            ...mix_getServerError('amount'),
            ...commonValidationTranslations,
          }"
          :size="size"
          :current-type="tradeType"
          autocomplete="off"
          :disabled="!logged"
          class="buy-sell-control__amount mb-16"
          type="number"
          step="0.00000001"
          @input="onChangeInputAmount"
          @blur="$v.amount.$touch()"
        />
      </div>
      <trade-buy-sell-amount-picker
        :buttons="buttons"
        class="buy-sell-control__percentage mb-16"
        @click="amountQuickPicker"
      />
      <trade-buy-sell-input
        v-model="total"
        :field="$v.total"
        :placeholder="$t('trade.buySell.total')"
        :currency="secondTicker"
        :optional-errors="{
          ...mix_getServerError('total'),
          ...commonValidationTranslations,
        }"
        :size="size"
        autocomplete="off"
        class="buy-sell-control__total mb-20"
        type="number"
        step="0.00000001"
        @input="onChangeInputTotal"
        @blur="$v.total.$touch()"
      />
      <exc-button
        v-if="logged"
        :color="submitTheme"
        :loading="loading"
        :disabled="$v.$error"
        size="xs"
        class="buy-sell-control__btn"
        full-width
        type="submit"
      >
        {{ `${formattedTradeType} ${firstTicker}` }}
      </exc-button>
      <exc-button
        v-else
        color="main"
        outline
        :loading="loading"
        size="xs"
        class="buy-sell-control__btn"
        :to="localePath('/login')"
        full-width
      >
        {{ $t('trade.loginBtn') }}
      </exc-button>
    </form>
  </div>
</template>

<script>
import { ExcButton } from '@/components/common'
import * as lockr from 'lockr'
import { Decimal } from 'decimal.js-light'
import { mapGetters, mapState } from 'vuex'
import {
  BLOCKED_BUY_SELL_PRICE_TICKERS,
  BUY_SELL_FORM_LIMIT,
  MARGIN_TRADE_CROSS,
  TRADE_TYPE,
  GENERAL_DECIMAL_PLACES,
} from '@/lib/constants'
import { ConfirmationModal } from '@/lib/ConfirmationModal'
import { validationMixin } from 'vuelidate'
import { required, minValue, maxValue } from 'vuelidate/lib/validators'
import { serverFormMixin } from '@/mixins/serverFormMixin'
import {
  capitalizeFirstLetter,
  valueSetterCutter,
  noExponents,
} from '@/lib/utils'
import {
  getFeeIncludingPlc,
  calculateTotalFromTotalWithoutFee,
} from '@/store/buySell'

export default {
  name: 'BuySellControl',
  components: {
    ExcButton,
    TradeBuySellHeader: () =>
      import(
        /* webpackMode: "eager" */ '@/components/trade/trade-buy-sell-elements/TradeBuySellHeader.vue'
      ),
    TradeBuySellInput: () =>
      import(
        /* webpackMode: "eager" */ '@/components/trade/trade-buy-sell-elements/TradeBuySellInput.vue'
      ),
    TradeBuySellAmountPicker: () =>
      import(
        '@/components/trade/trade-buy-sell-elements/TradeBuySellAmountPicker.vue'
      ),
  },
  mixins: [validationMixin, serverFormMixin],
  props: {
    tradeType: {
      type: String,
      required: true,
    },
    size: {
      type: String,
      default: null,
    },
  },
  data () {
    return {
      MARGIN_TRADE_CROSS,
      currentMarginType: 'borrow',
      buttons: [
        { name: '25%', type: 0.25 },
        { name: '50%', type: 0.5 },
        { name: '75%', type: 0.75 },
        { name: '100%', type: 1 },
      ],
      loading: false,
      // formKey: `buySell/${this.tradeType}Form`,
      // confirmationState: lockr.get(
      //     'trade-confirmation-state',
      //     1
      // ),
      // focusedField: '',
      // tooltipConfig: {
      //     trigger: 'focus',
      //     alignment: {
      //         target: {
      //             horizontal: AlignType.Right,
      //             vertical: AlignType.Middle,
      //         },
      //         overlay: {
      //             horizontal: AlignType.Right,
      //             vertical: AlignType.Middle,
      //         },
      //     },
      // },
    }
  },
  computed: {
    ...mapState({
      logged: state => state.auth.loggedIn,
      field (_, getters) {
        return getters[`buySell/${this.tradeType}Form/field`]
      },
      clearErrorsTimeToWatch: state =>
        state.buySell.form.clearErrorsTimeToWatch,
    }),
    ...mapGetters([
      // "getMarketPriceInUSDByTicker",
      // "getCurrentPair",
      // "getCurrencyAvailableBalance",
    ]),
    ...mapGetters({
      getCurrentPair: 'pairs/getCurrentPair',
      getCurrencyAvailableBalance: 'wallet/getCurrencyAvailableBalance',
      getMarginBalanceByTicker: 'wallet/getMarginBalanceByTicker',
    }),
    currentPairName () {
      return this.getCurrentPair.pairName
    },
    price: {
      get () {
        // Для PLC и PLCE всегда подставлять значение Last price
        // if (this.priceInputDisabled) return this.getCurrentPair.market.price
        // return this.field("price")
        return this.valueGetter('price')
      },
      set (val) {
        this.valueSetter(val, 'setBuySellFormPrice')
        // this.mix_clearServerError('price')
        this.$v.$touch()
      },
    },
    amount: {
      get () {
        return this.valueGetter('amount')
      },
      set (val) {
        this.valueSetter(val, 'setBuySellFormAmount')
        // this.mix_clearServerError('amount')
        this.$v.$touch()
      },
    },
    total: {
      get () {
        return this.valueGetter('total')
      },
      set (val) {
        this.valueSetter(val, 'setBuySellFormTotal')
        // this.mix_clearServerError('total')
        this.$v.$touch()
      },
    },
    // radios() {
    //     return [{
    //         label: 'Normal',
    //         value: 'normal'
    //     }, {
    //         label: 'Borrow',
    //         value: 'borrow'
    //     }, {
    //         label: 'Repay',
    //         value: 'repay'
    //     }]
    // },
    // totalWithFee: {
    //     get() {
    //         return this.field("totalWithFee");
    //     },
    //     set(value) {
    //         // this.changeField({ field: "totalWithFee", value });
    //     },
    // },
    // submitting() {
    //     const tradeFormName = `${this.tradeType}Form`
    //     const tradeFormSubmitting = this.$store.state.buySell[tradeFormName].fetchStatus === 'loading'

    //     return tradeFormSubmitting || this.$_buySellExchangeMixin_exchangeFormSubmitting
    // },
    formattedTradeType () {
      return this.tradeType === TRADE_TYPE.BUY
        ? this.$t('trade.buy')
        : this.$t('trade.sell')
    },
    submitTheme () {
      return this.tradeType === TRADE_TYPE.BUY ? 'green' : 'red'
    },
    formattedBalance () {
      return ` ${
        this.balance.greaterThan(0)
          ? noExponents(this.balance.toDecimalPlaces(8).toNumber())
          : '0'
      } ${this.balanceTicker ? this.balanceTicker : ''}`
    },
    firstTicker () {
      return this.getCurrentPair.stockTicker
    },
    secondTicker () {
      return this.getCurrentPair.moneyTicker
    },
    balanceTicker () {
      return this.tradeType === TRADE_TYPE.BUY
        ? this.secondTicker
        : this.firstTicker
    },
    isMarginTrade () {
      return this.$store.state.trade.currentTradeType === MARGIN_TRADE_CROSS
    },
    balance () {
      if (this.isMarginTrade) {
        return this.getMarginBalanceByTicker(this.balanceTicker).marginBalance
      }
      return this.getCurrencyAvailableBalance(this.balanceTicker)
    },
    // localStorageKey() {
    //     return confirmationLocalStorageKey(this.tradeType);
    // },
    priceInputDisabled () {
      if (['PLCU_PLC', 'PLC_PLCU'].includes(this.currentPairName)) return false // not disable on this pair (Andrii said)
      // Для BLOCKED_BUY_SELL_PRICE_TICKERS на странице трейда в блоках покупки продажи инпут Price
      // заблокировать для ввода и всегда подставлять значение Last price
      return (
        BLOCKED_BUY_SELL_PRICE_TICKERS.includes(this.secondTicker) ||
        BLOCKED_BUY_SELL_PRICE_TICKERS.includes(this.firstTicker)
      )
    },
    fee () {
      return getFeeIncludingPlc(
        this.getCurrentPair.stockTicker, // currentPairLeftSide
        this.$store.state.buySell,
        this.tradeType,
      )
    },
    commonValidationTranslations () {
      return {
        maxValue: this.$t('validators.maxValue', {
          max:
            this.tradeType === TRADE_TYPE.BUY
              ? (
                  calculateTotalFromTotalWithoutFee(
                    TRADE_TYPE.BUY,
                    this.balance.toNumber(),
                    this.fee,
                  ) / this.price
                ).toFixed(GENERAL_DECIMAL_PLACES)
              : this.balance.toNumber(),
        }),
        required: this.$t('validators.required'),
        minValue: this.$t('validators.minValue', {
          min: this.getCurrentPair.minAmountStock,
        }),
        decimalOnly: this.$t('validators.onlyNumbers'),
      }
    },
  },
  watch: {
    currentPairName () {
      this.$v.$reset()
    },
    price () {
      this.mix_clearServerError('price')
    },
    amount () {
      this.mix_clearServerError('amount')
    },
    total () {
      this.mix_clearServerError('total')
    },
    // clearErrorsTimeToWatch() {
    //     // ???
    //     this.$v.$reset()
    // },
    // marketOrLimit(v) {
    //     if (v === 'market') {
    //         const tradeTypeUppercase = this.tradeType.charAt(0).toUpperCase() + this.tradeType.slice(1)
    //         this.$store.commit(`exchange/exchangeMarket${tradeTypeUppercase}Form/reset`);
    //     }
    // },
  },
  methods: {
    valueSetter (val, mutation) {
      const value = valueSetterCutter(val)
      this.$store.commit(`buySell/${mutation}`, {
        formName: BUY_SELL_FORM_LIMIT,
        type: this.tradeType,
        value,
        currentPair: this.getCurrentPair,
      })
    },
    valueGetter (prop) {
      return this.$store.state.buySell.form[BUY_SELL_FORM_LIMIT][
        this.tradeType
      ][prop]
      // const isLastZero = this.isLastZeroChecker(val)
      // if (prop === 'price') {
      //     console.log('isLastZero::', isLastZero, val)
      // }
      // return val
      // if (!val || isLastZero) return val
      // return new Decimal(val).toString()
    },
    // isLastZeroChecker(val) {
    //     const decimals = val.split('.')
    //     return val[val.length - 1] === '0' && (decimals[1] || []).length <= GENERAL_DECIMAL_PLACES
    // },
    async handleSubmit () {
      this.mix_clearAllServerErrors()
      this.$v.$touch()
      if (this.$v.$invalid) return

      if (lockr.get('trade-confirmation-state', 1)) {
        const translateProps = {
          amount: this.amount,
          amountTicker: this.firstTicker,
          totalWithFee: this.price,
          totalTicker: this.secondTicker,
        }
        const text =
          this.tradeType === TRADE_TYPE.BUY
            ? this.$t('common.confirmationModal.buyForText', translateProps)
            : this.$t('common.confirmationModal.sellForText', translateProps)

        const confirm = await ConfirmationModal.open({
          title: `${capitalizeFirstLetter(
            this.$t(`trade.${this.tradeType.toLowerCase()}`),
          )} ${this.firstTicker}`,
          text,
          confirmationImg: 'buy.svg',
        })
        if (!confirm) return
      }

      const body = {
        price: this.price,
        amount: this.amount,
        pair: this.getCurrentPair.pairName,
        direction: this.tradeType,
      }

      this.loading = true
      const { errors, variables } = this.isMarginTrade
        ? await this.$api.postCrossMarginOrder({ body })
        : await this.$api.postOrder({ body })
      // const { errors } = await api.postOrder({ body });
      this.$v.$reset()
      this.loading = false

      if (errors) {
        this.mix_setServerErrors(errors, variables)
        // this.mix_notifyServerErrors(errors)
        return
      }

      this.$v.$reset()

      // const submitProp = { payload: {
      //     shouldOpenConfirmation: lockr.get(
      //         'trade-confirmation-state', 1
      //     ),
      // }}
      // this.submit(submitProp);
    },
    // handleBalanceClick() {
    //     switch (this.tradeType) {
    //         case "sell":
    //             return (this.amount = this.balance);
    //         case "buy":
    //             return (this.totalWithFee = this.balance);
    //     }
    // },
    // handleLoseFocus(e, ref) {
    //     const value = e.target.value
    //     const field = this.$refs[ref].$attrs.name
    //     this.focusedField = '';
    //     this.clearErrors({field});
    //     this.changeField({
    //         field,
    //         value: new Decimal(parseFloat(value) || "0").toFixed(8),
    //     });
    // },
    // handleFocusField(e, ref) {
    //     const value = e.target.value
    //     const field = this.$refs[ref].$attrs.name
    //     this.focusedField = field;
    //     this.changeField({
    //         field,
    //         value: new Decimal(parseFloat(value) || "0").toFixed(8),
    //     });
    //     const price = this.getMarketPriceInUSDByTicker(`${this.secondTicker}_USD`)

    //     this.tooltipConfig = {
    //         ...this.tooltipConfig,
    //         content: this.secondTicker !== "USD"
    //             ? `$ ${parseNumber(new Decimal(parseFloat(value) || 0).mul(price), 2)}`
    //             : false,
    //     };
    // },
    onMarketClickFilterBtn ({ type }) {
      this.amount = this.balance.mul(type).toString()
    },
    changeAmountRange (val) {
      const value = new Decimal(this.balance)
        .mul(val / 100)
        .toFixed(GENERAL_DECIMAL_PLACES)
        .toString()

      if (this.tradeType === TRADE_TYPE.BUY) {
        if (this.getCurrentPair.last) {
          this.price = this.price || this.getCurrentPair.last
        }

        this.total = value
      }

      if (this.tradeType === TRADE_TYPE.SELL) {
        this.amount = valueSetterCutter(value)
      }
    },

    amountQuickPicker (btn) {
      // this.$_onClickFilterBtn(btn)
      // const currentPairLeftSide = this.$store.getters['getCurrentPair'].stockTicker
      // const fee = getFeeIncludingPlc(this.firstTicker, this.$store.state.buySell, this.tradeType)
      // const balance = this.balance

      const value = new Decimal(this.balance)
        .mul(btn.type)
        .toFixed(GENERAL_DECIMAL_PLACES)
        .toString()
      // const value = val.toString()
      // const value = val.minus(val.mul(fee)).toString()

      if (this.tradeType === TRADE_TYPE.BUY) {
        if (this.getCurrentPair.last) {
          this.price = this.price || this.getCurrentPair.last
        }

        this.total = value // buy for all what has in balance
      }

      if (this.tradeType === TRADE_TYPE.SELL) {
        this.amount = valueSetterCutter(value) // sell for all balance value
      }

      // this.tradeType === 'buy' && this.changeField({ field: "totalWithFee", value, });
      // this.tradeType === 'sell' && this.changeField({ field: "amount", value, });
    },
    onChangeInputPrice (val) {
      this.price = val
      this.$v.price.$touch()
      // this.$v.price.$reset()
      // const price = this.getMarketPriceInUSDByTicker(`${this.secondTicker}_USD`)
      // this.tooltipConfig = {
      //     ...this.tooltipConfig,
      //     content: this.secondTicker !== "USD"
      //         ? `$ ${parseNumber(new Decimal(parseFloat(value) || 0).mul(price), 2)}`
      //         : false,
      // };

      // save that input was changed by user in order to
      // prevent it from modification
      // const mutationName = `buySell/${this.tradeType}Form/setCustom`
      // this.$store.commit(mutationName, {
      //     field: 'price',
      //     value: {
      //         changedByUser: true
      //     },
      // })
      // markPriceFieldAsChanged(this)
    },
    onChangeInputAmount (val) {
      this.amount = val
      this.mix_clearServerError('amount')
      this.$v.amount.$touch()
      // this.$v.amount.$reset()
      // markPriceFieldAsChanged(this)
    },
    onChangeInputTotal (val) {
      this.total = val
      this.mix_clearServerError('total')
      this.$v.total.$touch()
      // this.$v.total.$reset()
      // markPriceFieldAsChanged(this)
    },
  },

  validations () {
    const onlyBuy =
      this.tradeType === TRADE_TYPE.BUY
        ? {
            amount: {
              maxValue: maxValue(
                valueSetterCutter(
                  calculateTotalFromTotalWithoutFee(
                    TRADE_TYPE.BUY,
                    this.balance.toNumber(),
                    this.fee,
                  ) / this.price,
                ),
              ),
            },
          }
        : {}

    const onlySell =
      this.tradeType === TRADE_TYPE.SELL
        ? { amount: { maxValue: maxValue(this.balance.toNumber()) } }
        : {}

    return this.mix_validationsServerWrapper({
      price: {
        required,
        decimalOnly: v => !/[+-]/.test(v),
      },
      amount: {
        required,
        minValue: minValue(this.getCurrentPair.minAmountStock),
        decimalOnly: v => !/[+-]/.test(v),
        ...onlySell.amount,
        ...onlyBuy.amount,
      },
      total: {
        required,
      },
    })
  },
}

// function markPriceFieldAsChanged(ctx) {
//     // save that input was changed by user in order to
//     // prevent it from modification
//     const mutationName = `buySell/${ctx.tradeType}Form/setCustom`
//     ctx.$store.commit(mutationName, {
//         field: 'price',
//         value: {
//             changedByUser: true
//         },
//     })
// }
</script>

<style lang="scss" scoped>
.buy-sell-control {
  $root: &;

  // &::v-deep {
  //     .exc-input-label-error-wrapper__content {
  //         margin-bottom: toRem(20px);
  //     }
  // }

  // &__flex {

  //     @include bp() {
  //         display: flex;
  //         justify-content: space-between;
  //         align-items: center;
  //         flex-wrap: wrap;
  //         // grid-template-columns: repeat(2, 1fr);
  //         column-gap: toRem(10px);
  //     }

  //      @include bp(xl) {
  //          display: block;
  //      }
  // }

  // &__price,
  // &__amount {
  //     @include bp(l) {
  //         width: calc(50% - #{toRem(10px)});
  //     }

  //      @include bp(xl) {
  //          width: 100%;
  //      }
  // }

  &__total {
    @include bp(l) {
      width: 100%;
    }
  }

  &__percentage {
    width: 100%;
  }

  &--limit {
    #{$root}__total,
    #{$root}__percentage {
      // @include bp(l) {
      //     width: calc(50% - #{toRem(10px)});
      // }

      // @include bp(xl) {
      //     width: 100%;
      // }
    }
  }

  // &__item {
  //     min-height: 30px;
  // }
}
</style>
